import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  ShoppingBag,
  Clock,
  Users,
  Star,
  Download,
  ArrowRight,
  MessageCircle,
  BookOpen,
  FileText,
  Sparkles,
  Tag,
} from 'lucide-react';
import { Helmet } from 'react-helmet';
import RegistrationModal from '../components/whatsappclass/RegistrationModal';
import {
  Product,
  ProductCategory,
  categoryLabels,
} from '../data/products';
import { listPublishedProducts, staticProductFallback } from '../lib/products';

type CategoryFilter = 'all' | ProductCategory;
type PriceFilter = 'all' | 'free' | 'paid';

const heroBgImage =
  'https://images.unsplash.com/photo-1607082349566-187342175e2f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80';

const categoryIcons: Record<ProductCategory, React.ElementType> = {
  'whatsapp-class': MessageCircle,
  'short-course': Sparkles,
  ebook: BookOpen,
  resource: FileText,
};

const Products: React.FC = () => {
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [priceFilter, setPriceFilter] = useState<PriceFilter>('all');
  const [query, setQuery] = useState('');
  const [catalogue, setCatalogue] = useState<Product[]>(() => staticProductFallback());
  const [selected, setSelected] = useState<{ id: string; title: string; price: string } | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    listPublishedProducts()
      .then((items) => {
        if (!cancelled) setCatalogue(items);
      })
      .catch(() => {
        // The static catalogue keeps the public page usable before the
        // products migration is applied in a local/preview environment.
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    return catalogue.filter((p) => {
      if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
      if (priceFilter === 'free' && !p.isFree) return false;
      if (priceFilter === 'paid' && p.isFree) return false;
      if (query) {
        const q = query.toLowerCase();
        if (
          !p.title.toLowerCase().includes(q) &&
          !p.description.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [catalogue, categoryFilter, priceFilter, query]);

  const counts = useMemo(() => {
    return {
      total: catalogue.length,
      free: catalogue.filter((p) => p.isFree).length,
      paid: catalogue.filter((p) => !p.isFree).length,
      byCategory: (Object.keys(categoryLabels) as ProductCategory[]).reduce(
        (acc, k) => ({ ...acc, [k]: catalogue.filter((p) => p.category === k).length }),
        {} as Record<ProductCategory, number>
      ),
    };
  }, [catalogue]);

  const handleAction = (product: Product) => {
    if (product.isFree && product.downloadUrl) {
      window.open(product.downloadUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (product.isFree) {
      // Free ebook without direct link — capture lead via modal (uses NGN 0)
      setSelected({ id: product.id, title: product.title, price: '₦0' });
      return;
    }
    setSelected({ id: product.id, title: product.title, price: product.price });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Short Courses & Resources | Lagos School of Export Management</title>
        <meta
          name="description"
          content="Browse LSEM short courses, WhatsApp export classes, e-books and free resources. Filter by category and price to find the right learning product for you."
        />
        <link rel="canonical" href="https://lsem.org.ng/short-courses" />
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBgImage} alt="Short courses and digital learning resources" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-800/75" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-300 text-sm font-medium mb-6">
              <ShoppingBag className="h-4 w-4 mr-2" />
              Short Courses & Resources
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 leading-tight">
              Learn on Your Terms with{' '}
              <span className="text-blue-400">Short Courses</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed">
              Short courses, WhatsApp classes, e-books and free resources — all in one place. Buy once, learn anytime.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#catalog"
                className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                Browse Catalog
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-3 border border-slate-400 text-slate-200 hover:bg-slate-800/50 font-semibold rounded-lg transition-colors"
              >
                Talk to Us
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block">
            <path d="M0 80V40C240 0 480 0 720 20C960 40 1200 60 1440 40V80H0Z" fill="#f8fafc" />
          </svg>
        </div>
      </section>

      {/* Filters */}
      <section id="catalog" className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search short courses and resources..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-900"
                />
              </div>

              <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                {(['all', 'free', 'paid'] as PriceFilter[]).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPriceFilter(p)}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors capitalize ${
                      priceFilter === p
                        ? 'bg-white shadow text-slate-900'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {p === 'all' ? `All (${counts.total})` : p === 'free' ? `Free (${counts.free})` : `Paid (${counts.paid})`}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                onClick={() => setCategoryFilter('all')}
                className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  categoryFilter === 'all'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400'
                }`}
              >
                <Tag className="h-3.5 w-3.5 mr-1.5" />
                All Categories
              </button>
              {(Object.keys(categoryLabels) as ProductCategory[])
                .filter((cat) => counts.byCategory[cat] > 0)
                .map((cat) => {
                  const Icon = categoryIcons[cat];
                  const active = categoryFilter === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setCategoryFilter(cat)}
                      className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5 mr-1.5" />
                      {categoryLabels[cat]}
                      <span className={`ml-2 text-xs ${active ? 'text-blue-100' : 'text-slate-400'}`}>
                        {counts.byCategory[cat]}
                      </span>
                    </button>
                  );
                })}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
              <p className="text-slate-600">No short courses or resources match your filters yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} onAction={handleAction} />
              ))}
            </div>
          )}
        </div>
      </section>

      {selected && (
        <RegistrationModal
          isOpen={!!selected}
          onClose={() => setSelected(null)}
          course={selected}
        />
      )}
    </div>
  );
};

const ProductCard: React.FC<{ product: Product; onAction: (p: Product) => void }> = ({
  product,
  onAction,
}) => {
  const Icon = categoryIcons[product.category];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col">
      <div className="relative h-44">
        <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <span className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/95 text-slate-700">
          <Icon className="h-3.5 w-3.5 mr-1 text-blue-600" />
          {categoryLabels[product.category]}
        </span>
        <span
          className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold ${
            product.isFree ? 'bg-emerald-500 text-white' : 'bg-blue-600 text-white'
          }`}
        >
          {product.price}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="text-lg font-bold text-slate-900 leading-snug">{product.title}</h3>
        {product.author && (
          <p className="text-xs text-slate-500 mt-1">By {product.author}</p>
        )}
        <p className="text-sm text-slate-600 mt-2 line-clamp-3 flex-1">{product.description}</p>

        <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
          {product.hours != null && (
            <span className="inline-flex items-center">
              <Clock className="h-3.5 w-3.5 mr-1" />
              {product.hours}h
            </span>
          )}
          {product.students != null && (
            <span className="inline-flex items-center">
              <Users className="h-3.5 w-3.5 mr-1" />
              {product.students}
            </span>
          )}
          {product.downloads && (
            <span className="inline-flex items-center">
              <Download className="h-3.5 w-3.5 mr-1" />
              {product.downloads}
            </span>
          )}
          {product.rating != null && (
            <span className="inline-flex items-center">
              <Star className="h-3.5 w-3.5 mr-1 text-yellow-500 fill-current" />
              {product.rating.toFixed(1)}
            </span>
          )}
        </div>

        <button
          onClick={() => onAction(product)}
          className={`mt-5 inline-flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
            product.isFree
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {product.isFree ? (
            <>
              <Download className="h-4 w-4 mr-2" />
              {product.downloadUrl ? 'Download Now' : 'Get for Free'}
            </>
          ) : (
            <>
              Buy Now
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Products;